// =============================================================
// AdminCajas.jsx
// Gestión de cajas físicas / terminales POS
// Panel de Administración — Municipalidad de Pocito
// =============================================================

import { useState, useEffect, useCallback } from "react";
import {
  Monitor, Plus, Pencil, CheckCircle2,
  XCircle, Loader2, AlertCircle, Save, X
} from "lucide-react";

const API = process.env.REACT_APP_API_URL || "/api";

// ─────────────────────────────────────────────────────────────
// Badge de estado activo/inactivo
// ─────────────────────────────────────────────────────────────
const BadgeActiva = ({ activa }) =>
  activa ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
      <CheckCircle2 className="w-3 h-3" /> Activa
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200">
      <XCircle className="w-3 h-3" /> Inactiva
    </span>
  );

// ─────────────────────────────────────────────────────────────
// Modal para crear / editar caja
// ─────────────────────────────────────────────────────────────
const ModalCaja = ({ caja, onClose, onGuardado }) => {
  const esNueva = !caja?.id;

  const [form, setForm] = useState({
    codigo:    caja?.codigo    || "",
    nombre:    caja?.nombre    || "",
    ubicacion: caja?.ubicacion || "",
    activa:    caja?.activa    ?? 1,
  });
  const [guardando, setGuardando] = useState(false);
  const [error,     setError]     = useState(null);

  const handleChange = (campo, valor) =>
    setForm(prev => ({ ...prev, [campo]: valor }));

  const handleGuardar = async () => {
    if (!form.codigo.trim() || !form.nombre.trim()) {
      setError("El código y el nombre son obligatorios.");
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      const url    = esNueva ? `${API}/admin/cajas` : `${API}/admin/cajas/${caja.id}`;
      const method = esNueva ? "POST" : "PUT";
      const res    = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al guardar");
      onGuardado();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center">
              <Monitor className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-base font-bold text-slate-800">
              {esNueva ? "Nueva caja / POS" : "Editar caja"}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
              Código del dispositivo *
            </label>
            <input
              type="text"
              value={form.codigo}
              onChange={e => handleChange("codigo", e.target.value.toUpperCase())}
              disabled={!esNueva}
              placeholder="Ej: CAJA-01"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm
                         focus:outline-none focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
            />
            {!esNueva && (
              <p className="text-xs text-slate-400 mt-1">El código no se puede modificar.</p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
              Nombre descriptivo *
            </label>
            <input
              type="text"
              value={form.nombre}
              onChange={e => handleChange("nombre", e.target.value)}
              placeholder="Ej: Caja 1 - Planta Baja"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm
                         focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
              Ubicación / Sector
            </label>
            <input
              type="text"
              value={form.ubicacion}
              onChange={e => handleChange("ubicacion", e.target.value)}
              placeholder="Ej: Tesorería, Delegación Norte"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm
                         focus:outline-none focus:border-blue-500"
            />
          </div>

          {!esNueva && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
                Estado
              </label>
              <select
                value={form.activa}
                onChange={e => handleChange("activa", Number(e.target.value))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm
                           focus:outline-none focus:border-blue-500 bg-white"
              >
                <option value={1}>Activa</option>
                <option value={0}>Inactiva</option>
              </select>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            className="flex-1 border border-slate-200 text-slate-600 hover:bg-slate-50
                       font-semibold py-2.5 rounded-xl transition-colors text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            disabled={guardando}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50
                       text-white font-semibold py-2.5 rounded-xl transition-colors
                       flex items-center justify-center gap-2 text-sm"
          >
            {guardando
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</>
              : <><Save className="w-4 h-4" /> {esNueva ? "Crear caja" : "Guardar cambios"}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Componente principal AdminCajas
// ─────────────────────────────────────────────────────────────
const AdminCajas = ({ usuario }) => {
  const [cajas,        setCajas]        = useState([]);
  const [cargando,     setCargando]     = useState(true);
  const [error,        setError]        = useState(null);
  const [modalCaja,    setModalCaja]    = useState(null); // null | {} | {caja}
  const [confirmacion, setConfirmacion] = useState(null); // para toggle activa/inactiva

  const puedeEditar = usuario?.rol === "admin";

  // ── Carga de cajas ──────────────────────────────────────
  const cargarCajas = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res  = await fetch(`${API}/cajas`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al cargar cajas");
      setCajas(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargarCajas(); }, [cargarCajas]);

  // ── Toggle activa/inactiva ──────────────────────────────
  const toggleActiva = async (caja) => {
    try {
      const res = await fetch(`${API}/admin/cajas/${caja.id}`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ activa: caja.activa ? 0 : 1 }),
      });
      if (!res.ok) throw new Error("Error al actualizar");
      await cargarCajas();
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirmacion(null);
    }
  };

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Header de sección */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Cajas / Terminales POS</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Administración de cajas físicas asociadas a pagos
          </p>
        </div>
        {puedeEditar && (
          <button
            onClick={() => setModalCaja({})}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700
                       text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nueva caja
          </button>
        )}
      </div>

      {/* Error global */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading */}
      {cargando && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-sm text-slate-500">Cargando cajas...</span>
        </div>
      )}

      {/* Tabla */}
      {!cargando && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Código
                </th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Nombre
                </th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Ubicación
                </th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Estado
                </th>
                {puedeEditar && (
                  <th className="text-right px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Acciones
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {cajas.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-slate-400 text-sm">
                    No hay cajas registradas.
                  </td>
                </tr>
              ) : (
                cajas.map(caja => (
                  <tr key={caja.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3.5">
                      <span className="font-mono text-xs bg-slate-100 text-slate-700
                                       px-2 py-1 rounded-lg font-semibold">
                        {caja.codigo}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 font-medium text-slate-700">
                      {caja.nombre}
                    </td>
                    <td className="px-5 py-3.5 text-slate-500">
                      {caja.ubicacion || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-3.5">
                      <BadgeActiva activa={caja.activa} />
                    </td>
                    {puedeEditar && (
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-2">
                          {/* Editar */}
                          <button
                            onClick={() => setModalCaja(caja)}
                            className="p-1.5 text-slate-400 hover:text-blue-600
                                       hover:bg-blue-50 rounded-lg transition-colors"
                            title="Editar caja"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {/* Toggle activa */}
                          <button
                            onClick={() => setConfirmacion(caja)}
                            className={`p-1.5 rounded-lg transition-colors
                              ${caja.activa
                                ? "text-slate-400 hover:text-red-500 hover:bg-red-50"
                                : "text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"
                              }`}
                            title={caja.activa ? "Desactivar caja" : "Activar caja"}
                          >
                            {caja.activa
                              ? <XCircle className="w-4 h-4" />
                              : <CheckCircle2 className="w-4 h-4" />
                            }
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Crear / Editar */}
      {modalCaja !== null && (
        <ModalCaja
          caja={modalCaja?.id ? modalCaja : null}
          onClose={() => setModalCaja(null)}
          onGuardado={cargarCajas}
        />
      )}

      {/* Confirm toggle activa */}
      {confirmacion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center
                ${confirmacion.activa ? "bg-red-100" : "bg-emerald-100"}`}>
                {confirmacion.activa
                  ? <XCircle className="w-5 h-5 text-red-600" />
                  : <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                }
              </div>
              <div>
                <p className="font-bold text-slate-800 text-sm">
                  {confirmacion.activa ? "Desactivar caja" : "Activar caja"}
                </p>
                <p className="text-slate-500 text-xs">{confirmacion.nombre}</p>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              {confirmacion.activa
                ? "La caja quedará inactiva y no podrá registrar nuevos pagos. ¿Confirmar?"
                : "La caja volverá a estar disponible para registrar pagos. ¿Confirmar?"
              }
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmacion(null)}
                className="flex-1 border border-slate-200 text-slate-600 font-semibold
                           py-2.5 rounded-xl hover:bg-slate-50 transition-colors text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={() => toggleActiva(confirmacion)}
                className={`flex-1 font-semibold py-2.5 rounded-xl transition-colors text-sm text-white
                  ${confirmacion.activa
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
              >
                {confirmacion.activa ? "Desactivar" : "Activar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCajas;
